/**
 * Time Tracking Service for Lexy Word Add-in
 * 
 * Tracks active working time in the document.
 * Features:
 * - Starts when add-in is opened
 * - Pauses after 2 minutes of inactivity
 * - Resumes on activity
 * - Saves to case in Lexy
 */

interface TimeTrackingState {
  isTracking: boolean;
  isPaused: boolean;
  totalSeconds: number;
  formattedTime: string;
  startTime: Date | null;
}

type TimeTrackingListener = (state: TimeTrackingState) => void;

class TimeTrackingService {
  private isTracking: boolean = false;
  private isPaused: boolean = false;
  private startTime: Date | null = null;
  private totalSeconds: number = 0;
  private lastActivityTime: Date | null = null;
  private pauseThresholdMs: number = 2 * 60 * 1000; // 2 minutes
  private listeners: TimeTrackingListener[] = [];
  private checkInterval: ReturnType<typeof setInterval> | null = null;
  private tickInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Bind methods
    this.handleActivity = this.handleActivity.bind(this);
  }

  /**
   * Start tracking time
   */
  start(): void {
    if (this.isTracking) return;
    
    this.isTracking = true;
    this.isPaused = false;
    this.startTime = new Date();
    this.lastActivityTime = new Date();
    
    // Set up activity listeners
    if (typeof window !== 'undefined') {
      window.addEventListener('mousemove', this.handleActivity);
      window.addEventListener('keydown', this.handleActivity);
      window.addEventListener('click', this.handleActivity);
      window.addEventListener('scroll', this.handleActivity);
    }
    
    // Check for inactivity every 10 seconds
    this.checkInterval = setInterval(() => this.checkActivity(), 10000);
    
    // Tick every second to update display
    this.tickInterval = setInterval(() => this.tick(), 1000);
    
    console.log('TimeTracking: Started');
    this.notifyListeners();
  }

  /**
   * Stop tracking completely
   */
  stop(): void {
    if (!this.isTracking) return;
    
    this.isTracking = false;
    this.isPaused = false;
    
    // Remove listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('mousemove', this.handleActivity);
      window.removeEventListener('keydown', this.handleActivity);
      window.removeEventListener('click', this.handleActivity);
      window.removeEventListener('scroll', this.handleActivity);
    }
    
    // Clear intervals
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
    
    console.log('TimeTracking: Stopped. Total:', this.formatTime(this.totalSeconds));
    this.notifyListeners();
  }

  /**
   * Pause tracking (manual or due to inactivity)
   */
  pause(): void {
    if (!this.isTracking) return;
    
    this.isPaused = true;
    console.log('TimeTracking: Paused');
    this.notifyListeners();
  }

  /**
   * Resume tracking
   */
  resume(): void {
    if (!this.isTracking) return;
    
    this.isPaused = false;
    this.lastActivityTime = new Date();
    console.log('TimeTracking: Resumed');
    this.notifyListeners();
  }

  /**
   * Toggle pause/resume
   */
  toggle(): void {
    if (this.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  /**
   * Handle user activity
   */
  handleActivity(): void {
    this.lastActivityTime = new Date();
    if (this.isPaused) {
      this.resume();
    }
  }

  /**
   * Check for inactivity
   */
  checkActivity(): void {
    if (!this.isTracking || this.isPaused || !this.lastActivityTime) return;
    
    const now = new Date();
    const timeSinceActivity = now.getTime() - this.lastActivityTime.getTime();
    
    if (timeSinceActivity >= this.pauseThresholdMs) {
      this.pause();
    }
  }

  /**
   * Tick - update total seconds
   */
  tick(): void {
    if (!this.isTracking || this.isPaused) return;
    
    this.totalSeconds++;
    this.notifyListeners();
  }

  /**
   * Reset the timer
   */
  reset(): void {
    this.totalSeconds = 0;
    this.startTime = new Date();
    this.notifyListeners();
  }

  /**
   * Get current state
   */
  getState(): TimeTrackingState {
    return {
      isTracking: this.isTracking,
      isPaused: this.isPaused,
      totalSeconds: this.totalSeconds,
      formattedTime: this.formatTime(this.totalSeconds),
      startTime: this.startTime
    };
  }

  /**
   * Format seconds to HH:MM:SS or MM:SS
   */
  formatTime(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Format for display (e.g., "1t 23m" or "45m")
   */
  formatTimeShort(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    
    if (hrs > 0) {
      return `${hrs}t ${mins}m`;
    }
    if (mins > 0) {
      return `${mins}m`;
    }
    return `${seconds}s`;
  }

  /**
   * Add listener for state changes
   */
  addListener(callback: TimeTrackingListener): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(): void {
    const state = this.getState();
    this.listeners.forEach(cb => cb(state));
  }

  /**
   * Get data for saving to Lexy
   */
  getTimeEntry(): {
    start_time: string | undefined;
    end_time: string;
    duration_seconds: number;
    duration_formatted: string;
  } {
    return {
      start_time: this.startTime?.toISOString(),
      end_time: new Date().toISOString(),
      duration_seconds: this.totalSeconds,
      duration_formatted: this.formatTime(this.totalSeconds)
    };
  }
}

// Singleton instance
export const timeTrackingService = new TimeTrackingService();
export default timeTrackingService;
